import { useEffect, useState, type ChangeEvent } from 'react'
import { api, type InvitableAccount, type RoleOption } from '../lib/api.ts'
import { offeredRoleNames } from '../lib/invite.ts'
import { Button } from '../components/Button.tsx'
import { Checkbox } from '../components/Checkbox.tsx'
import { EntityForm, Field, FieldGroup } from '../components/EntityForm.tsx'
import { Select } from '../components/Select.tsx'
import { icons } from '../components/icons.ts'
import { useConfirm } from '../components/Confirm.tsx'
import { useToast } from '../components/Toast.tsx'

/** Mirrors `SUPER_ADMIN_DOMAIN` on the server, which is the real check. */
const SUPER_ADMIN_DOMAIN = '@quave.com.br'

/**
 * Inviting somebody in (ADR 0024).
 *
 * The person arrives with the função chosen here, so they are not a member
 * who sees nothing until somebody finds them on the list. A member whose role
 * may invite invites into their own pharmacy; only a super admin names
 * pharmacies, and only a super admin makes another -- who sees everything
 * whatever the função, so for them it is optional. The server decides all of
 * it again; this only avoids offering what it would refuse.
 */
export function InviteUser({ onBack }: { onBack: () => void }) {
  const [accounts, setAccounts] = useState<InvitableAccount[]>([])
  const [roles, setRoles] = useState<RoleOption[]>([])
  const [grantable, setGrantable] = useState<string[]>([])
  const [loaded, setLoaded] = useState(false)
  const [error, setError] = useState<string | undefined>()

  const [email, setEmail] = useState('')
  const [superAdmin, setSuperAdmin] = useState(false)
  const [roleName, setRoleName] = useState('')
  const [chosen, setChosen] = useState<string[]>([])
  const [busy, setBusy] = useState(false)
  const toast = useToast()
  const confirm = useConfirm()

  useEffect(() => {
    void api
      .inviteForm()
      .then((r) => {
        setAccounts(r.accounts)
        setRoles(r.roles)
        setGrantable(r.grantable)
        setLoaded(true)
      })
      .catch((cause: unknown) =>
        setError(cause instanceof Error ? cause.message : 'falha ao carregar'),
      )
  }, [])

  const wrongDomain = superAdmin && !email.trim().toLowerCase().endsWith(SUPER_ADMIN_DOMAIN)
  // Only a super admin gets a list of pharmacies, and a super admin invite
  // needs none: the role sees every account already.
  const mustChoose = accounts.length > 0 && !superAdmin
  /*
   * The funções the person can be given: the names every pharmacy they are
   * going into has, since each pharmacy has its own rows and the server looks
   * the name up in each. A pharmacy's manager is sent their own only.
   */
  const offered = offeredRoleNames(roles, mustChoose ? chosen : undefined)
  const ready =
    email.trim().includes('@') &&
    !wrongDomain &&
    (superAdmin || offered.includes(roleName)) &&
    (!mustChoose || chosen.length > 0)

  async function send() {
    /*
     * A super admin is confirmed, nothing else is: it is the one grant that
     * sees every pharmacy, and confirming the ordinary ones too would train
     * people to click through the dialog that matters.
     */
    if (superAdmin) {
      const sure = await confirm({
        title: `Criar ${email.trim()} como super admin?`,
        message:
          'Vai enxergar todas as farmácias — receitas, simulações e o conjunto de referência — e poderá criar outros super admins. É um papel da equipe que constrói o sistema.',
        confirmLabel: 'Sim, criar super admin',
        tone: 'danger',
      })
      if (!sure) return
    }
    setBusy(true)
    try {
      await api.invite({
        email: email.trim(),
        role: superAdmin ? 'super_admin' : 'pharmacist',
        accountIds: superAdmin ? [] : chosen,
        ...(superAdmin ? {} : { roleName }),
      })
      toast.show('Convite enviado', { tone: 'success' })
      onBack()
    } catch (cause) {
      toast.show(cause instanceof Error ? cause.message : 'falha ao convidar', { tone: 'error' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex flex-col gap-5 p-5 pt-0">
      <div className="flex flex-wrap items-center gap-3">
        <Button
          variant="tertiary"
          tone="neutral"
          size="small"
          leadingIcon={icons.back}
          onClick={onBack}
        >
          Usuários
        </Button>
        <h1 className="text-tH4 text-text-strong">Convidar</h1>
      </div>

      {error !== undefined && <p className="text-tP text-text-error">{error}</p>}

      {!loaded ? (
        error === undefined && <p className="text-tP text-text-weak">Carregando…</p>
      ) : (
        <EntityForm
          description="A pessoa recebe um código por e-mail ao entrar, e chega com a função escolhida aqui."
          onSave={() => void send()}
          saving={busy}
          dirty={ready}
          saveLabel="Convidar"
        >
          <Field
            label="E-mail"
            value={email}
            editable
            onChange={setEmail}
            {...(wrongDomain
              ? { helperText: `Um super admin precisa de um endereço ${SUPER_ADMIN_DOMAIN}.` }
              : {})}
          />
          {/* Only a super admin may make another, so only they are asked. */}
          {grantable.includes('super_admin') && (
            <FieldGroup
              label="Super admin"
              wide={false}
              helperText="Da equipe que constrói o sistema: vê e faz tudo, qualquer que seja a função."
            >
              <label className="flex items-center gap-2">
                <Checkbox checked={superAdmin} onChange={(e) => setSuperAdmin(e.target.checked)} />
                <span className="text-tP text-text-strong">Sim</span>
              </label>
            </FieldGroup>
          )}
          {mustChoose && (
            <FieldGroup
              label="Farmácias"
              helperText="Em cada uma, a pessoa recebe a função escolhida abaixo."
            >
              <div className="flex flex-wrap gap-x-6 gap-y-2">
                {accounts.map((account) => (
                  <label key={account.id} className="flex items-center gap-2">
                    <Checkbox
                      checked={chosen.includes(account.id)}
                      onChange={(e) => {
                        setChosen((current) =>
                          e.target.checked
                            ? [...current, account.id]
                            : current.filter((one) => one !== account.id),
                        )
                        // What is offered depends on the pharmacies, so it is chosen again.
                        setRoleName('')
                      }}
                    />
                    <span className="text-tP text-text-strong">
                      {account.name}
                      {/* Ours, not a customer's: labelled rather than hidden,
                          because inviting into it is occasionally right. */}
                      {account.internal && <span className="text-text-weak"> (interna)</span>}
                    </span>
                  </label>
                ))}
              </div>
            </FieldGroup>
          )}
          {!superAdmin && (
            <Select
              label="Função"
              value={roleName}
              helperText={
                mustChoose && chosen.length === 0
                  ? 'Escolha as farmácias primeiro.'
                  : 'Quais conferências a pessoa vai enxergar.'
              }
              onChange={(e: ChangeEvent<HTMLSelectElement>) => setRoleName(e.target.value)}
            >
              <option value="" disabled>
                Escolha…
              </option>
              {offered.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </Select>
          )}
          {/* Said before sending, so nobody lands in a pharmacy by accident. */}
          <p className="text-tSmall text-text-weak md:col-span-2">
            {superAdmin
              ? 'Um super admin vê todas as farmácias.'
              : !mustChoose
                ? 'A pessoa vai entrar na sua farmácia.'
                : chosen.length === 0
                  ? 'Escolha em quais farmácias a pessoa vai entrar.'
                  : `A pessoa vai entrar em: ${accounts
                      .filter((account) => chosen.includes(account.id))
                      .map((account) => account.name)
                      .join(', ')}${roleName === '' ? '' : `, como ${roleName}`}.`}
          </p>
        </EntityForm>
      )}
    </div>
  )
}
